import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap, Users, RefreshCw, TrendingUp, Layers, ShieldCheck, LucideIcon } from 'lucide-react';
import { useLandingTheme } from '../hooks/useLandingTheme';

interface Feature {
  titleKey: string;
  descriptionKey: string;
  imageKey: string;
  imageLeft: boolean;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  iconBgDark: string;
}

const features: Feature[] = [
  {
    titleKey: 'landing.features.smartAutomation.title',
    descriptionKey: 'landing.features.smartAutomation.description',
    imageKey: 'smart-automation',
    imageLeft: false,
    icon: Zap,
    iconColor: '#2B7FFF',
    iconBg: '#EFF6FF',
    iconBgDark: '#162456',
  },
  {
    titleKey: 'landing.features.audienceTargeting.title',
    descriptionKey: 'landing.features.audienceTargeting.description',
    imageKey: 'audience-targeting',
    imageLeft: true,
    icon: Users,
    iconColor: '#AD46FF',
    iconBg: '#FAF5FF',
    iconBgDark: '#3C0366',
  },
  {
    titleKey: 'landing.features.automatedEngagement.title',
    descriptionKey: 'landing.features.automatedEngagement.description',
    imageKey: 'automated-engagement',
    imageLeft: false,
    icon: RefreshCw,
    iconColor: '#F43F5E',
    iconBg: '#FFF1F2',
    iconBgDark: '#510424',
  },
  {
    titleKey: 'landing.features.growthAnalytics.title',
    descriptionKey: 'landing.features.growthAnalytics.description',
    imageKey: 'growth-analytics',
    imageLeft: true,
    icon: TrendingUp,
    iconColor: '#10B981',
    iconBg: '#ECFDF5',
    iconBgDark: '#022C22',
  },
  {
    titleKey: 'landing.features.multiAccount.title',
    descriptionKey: 'landing.features.multiAccount.description',
    imageKey: 'multi-account',
    imageLeft: false,
    icon: Layers,
    iconColor: '#2B7FFF',
    iconBg: '#EFF6FF',
    iconBgDark: '#162456',
  },
  {
    titleKey: 'landing.features.safeSecurity.title',
    descriptionKey: 'landing.features.safeSecurity.description',
    imageKey: 'safe-secure',
    imageLeft: true,
    icon: ShieldCheck,
    iconColor: '#0EA5E9',
    iconBg: '#F0F9FF',
    iconBgDark: '#053345',
  },
];

const FeatureRow: React.FC<{ feature: Feature; isDark: boolean }> = ({ feature, isDark }) => {
  const { t } = useTranslation();
  const Icon = feature.icon;

  const imageSuffix = isDark ? 'dark' : 'light';

  const imageBlock = (
    <div className="flex items-center justify-center">
      <img
        src={`/images/landing/${feature.imageKey}-${imageSuffix}.png.png`}
        alt={t(feature.titleKey)}
        className="w-full max-w-[500px] h-auto rounded-2xl"
      />
    </div>
  );

  const textBlock = (
    <div className="flex flex-col justify-center">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
        style={{ backgroundColor: isDark ? feature.iconBgDark : feature.iconBg }}
      >
        <Icon size={20} style={{ color: feature.iconColor }} />
      </div>
      <h3
        className="font-geist font-bold text-2xl lg:text-3xl mb-4"
        style={{ color: isDark ? '#E2E8F0' : '#314158' }}
      >
        {t(feature.titleKey)}
      </h3>
      <p
        className="font-geist text-base lg:text-lg leading-7"
        style={{ color: isDark ? '#90A1B9' : '#62748E' }}
      >
        {t(feature.descriptionKey)}
      </p>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center py-8 lg:py-20">
      {feature.imageLeft ? (
        <>
          {imageBlock}
          {textBlock}
        </>
      ) : (
        <>
          <div className="order-2 lg:order-1">{textBlock}</div>
          <div className="order-1 lg:order-2">{imageBlock}</div>
        </>
      )}
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();
  const isDark = useLandingTheme();

  return (
    <section
      id="features"
      className="py-20 lg:py-28"
      style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-20">
          <h2
            className="font-geist font-bold text-2xl md:text-3xl lg:text-4xl mb-4"
            style={{ color: isDark ? '#E2E8F0' : '#314158' }}
          >
            {t('landing.features.title')}
          </h2>
          <p
            className="font-geist text-base md:text-lg max-w-2xl mx-auto"
            style={{ color: isDark ? '#90A1B9' : '#62748E' }}
          >
            {t('landing.features.subtitle')}
          </p>
        </div>

        {/* Feature Rows */}
        <div className="max-w-[1200px] mx-auto">
          {features.map((feature, index) => (
            <FeatureRow key={index} feature={feature} isDark={isDark} />
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-8">
          <Link
            to="/registration"
            className="gradient-btn inline-flex items-center justify-center bg-fc-gradient-btn text-white font-geist font-medium rounded-xl px-6 py-3 text-sm"
          >
            {t('landing.hero.startTrial')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
